import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtDT, fmtWindow, usd, STATUS } from "../../lib/api";
import { AuthImg } from "../../components/Photos";

const H = ({ children }) => <h2 className="font-head text-lg uppercase font-semibold border-b border-neutral-700 mt-6 mb-2 pb-1">{children}</h2>;
const METHOD = { in_person_signature: "In-person signature", written_attachment: "Written approval attached", declined: "Customer declined", absent: "Customer absent" };

function Phase({ job, phase }) {
  const c = job.condition?.[phase];
  const items = job.checklists[phase];
  const photos = job.photos.filter((p) => p.phase === phase);
  return (
    <div className="avoid-break">
      <H>{phase === "before" ? "Before work" : "After work"} — {c?.status === "submitted" ? `submitted ${fmtDT(c.submitted_at)} by ${c.submitted_by}` : c ? "draft" : "not started"}</H>
      {c && (
        <div className="text-sm space-y-1">
          {phase === "before" && <div>Odometer: {c.odometer || "—"} · Warning lights: {c.warning_lights || "—"}</div>}
          {phase === "after" && <><div>Completed work: {c.completed_work || "—"}</div><div>Unresolved: {c.unresolved_issues || "—"}</div><div>New damage/incidents: {c.new_damage || "—"}</div></>}
          <table className="w-full text-xs mt-2"><tbody>
            {items.map((i) => <tr key={i.key} className="border-b border-neutral-800"><td className="py-1">{i.label}</td><td className="uppercase">{c.checklist?.[i.key]?.status || "—"}</td><td>{c.checklist?.[i.key]?.note}</td></tr>)}
          </tbody></table>
          {Object.entries(c.slot_exceptions || {}).filter(([, v]) => v).map(([k, v]) => <div key={k} className="text-xs">Photo not taken ({k}): {v}</div>)}
          {c.notes && <div>Notes: {c.notes}</div>}
        </div>
      )}
      <div className="grid grid-cols-4 gap-2 mt-2">
        {photos.map((p) => (
          <div key={p.id} className="text-[10px]">
            <AuthImg id={p.id} className="w-full aspect-square rounded" />
            <div>{p.slot} {p.caption && `· ${p.caption}`}</div>
            <div>Uploaded {fmtDT(p.uploaded_at)}</div>
          </div>
        ))}
      </div>
      {(job.corrections || []).filter((x) => x.phase === phase).map((x) => <div key={x.id} className="text-xs mt-1">Correction — {x.field}: {x.value} (reason: {x.reason}; {x.author}, {fmtDT(x.at)})</div>)}
    </div>
  );
}

export default function Report() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get(`/jobs/${id}`).then(({ data }) => setJob(data)).catch(() => setErr(true));
  }, [id]);
  if (err) return <div className="p-6 text-neutral-400" data-testid="report-not-found">Job not found. <Link to="/a/jobs" className="text-[#FF6B00]">Back</Link></div>;
  if (!job) return <div className="p-6 text-neutral-400">Loading…</div>;
  const c = job.customer;
  return (
    <div className="print-page min-h-screen bg-[#0A0A0A] text-neutral-100 p-6 max-w-3xl mx-auto text-sm" data-testid="printable-report">
      <div className="no-print flex justify-between mb-4">
        <Link to={`/a/jobs/${id}`} className="text-neutral-400">← Back</Link>
        <button onClick={() => window.print()} className="h-10 px-4 rounded-xl bg-[#FF6B00] text-white font-semibold" data-testid="print-report-btn">Print / Save PDF</button>
      </div>
      <h1 className="font-head text-3xl font-bold uppercase">Driven Auto Care — Job #{job.number}</h1>
      <div className="text-neutral-400">{job.service_name} · {STATUS[job.status]} · Report generated {fmtDT(new Date().toISOString())}</div>
      <H>Job</H>
      <div>Customer: {c.name} · {c.phone} · {[c.address, c.city, c.state || "OH", c.zip].filter(Boolean).join(", ")}</div>
      <div>Vehicle: {job.vehicle_label} {job.vehicle.engine}</div>
      <div>Window: {fmtWindow(job.window_start, job.window_end)} · Specialist: {job.assigned_name || "—"}</div>
      <div>Scope: {job.quotes[job.quotes.length - 1].scope}</div>
      <H>Quotes</H>
      {job.quotes.map((q) => <div key={q.version}>v{q.version}: subtotal {usd(q.pricing.subtotal)}, tax {usd(q.pricing.tax_amount)}, total {usd(q.pricing.total)} — {q.reason} ({q.by}, {fmtDT(q.at)})</div>)}
      <H>Approval records</H>
      {!job.authorizations.length && <div>None recorded.</div>}
      {job.authorizations.map((a) => (
        <div key={a.id} className="avoid-break mb-2">
          <div>{a.kind === "work" ? "Work authorization" : "Completion acknowledgment"} — {METHOD[a.method]} {a.customer_name && `· ${a.customer_name}`} · quote v{a.quote_version} · total {usd(a.total)}</div>
          <div className="text-xs">Recorded by {a.actor_name} ({a.actor_role}), server time {fmtDT(a.at)} {a.note && `· "${a.note}"`}</div>
          {a.signature && <img src={a.signature} alt="signature" className="h-14 rounded border border-[#262626]" />}
        </div>
      ))}
      <Phase job={job} phase="before" />
      <Phase job={job} phase="after" />
      {job.overrides.length > 0 && <><H>Admin exceptions</H>{job.overrides.map((o) => <div key={o.id}>{o.phase}: {o.reason} ({o.by}, {fmtDT(o.at)})</div>)}</>}
      <H>Timeline</H>
      {job.timeline.map((t, i) => <div key={i} className="text-xs">{fmtDT(t.at)} — {t.action} · {t.actor} {t.detail && `· ${t.detail}`}</div>)}
      <p className="text-[11px] mt-6 text-neutral-500">Photo upload times are server timestamps. Device-reported capture times are not verified and do not prove capture time or location. Checklists document work performed and do not certify the vehicle as safe.</p>
    </div>
  );
}
